import { css } from '@emotion/react'
import { Fade, Grow } from '@mui/material'
import {
    SyntheticEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { openMenuDialog } from '../../../../actions/menu'
import { goToNotes } from '../../../../actions/nav'
import {
    addFriend,
    bookmarkModel,
    removeBookmark,
    removeFriend,
} from '../../../../actions/user'
import { VideoType } from '../../../../constants/enums'
import { isTouchDevice } from '../../../../utilities/browser'
import ButtonModelMenu from '../../../Buttons/ButtonModelMenu'
// import { useModelColor } from '../../../Hooks/useModelColor'
import { openRoom, pinRoom, setLoungeSurrogate } from '../../../../actions/room'
import {
    clearAvatarInfoOpenID,
    clearAvatarInfoOpenTimer,
    resetAvatarInfoOpenIDState,
    setAvatarInfoOpenID,
    setLargePreviewPinnedIDState,
} from '../../../../app-state/state.grid'
import { type Size } from '../../../../app-state/state.screen'
import { SNAPVIDEOSTATE } from '../../../../state/state.snapvideo'
import {
    TransitionTypes,
    motionTransition,
} from '../../../../utilities/transitions'
import { useGridAvatarInfoOpenAppState } from '../../../Hooks/useGridAvatarInfoOpenAppState'
import { useGridLargePreviewPinnedAppState } from '../../../Hooks/useGridLargePreviewPinnedAppState'
import { useIsRoomPinned } from '../../../Hooks/useRoom'
import { useUserSelector } from '../../../Hooks/useUser'
import CameraIcon from '../../../Icons/Camera'
import NotesIcon from '../../../Icons/Notes'
import PinIcon from '../../../Icons/Pin'
import RoomIcon from '../../../Icons/Room'
import { palette } from '../../../styles/colors'
import layers from '../../../styles/layers'
import shape from '../../../styles/shape'
import {
    hideDragHelper,
    highlightDragHelper,
    showDragHelper,
} from '../../Chat/Rooms/PinnedRooms/PinDragHelper'
import LiveSnapStream from '../../LiveSnapStream'
import BookmarkBadge from '../Badges/Bookmark'
import FriendBadge from '../Badges/Friend'
import MissMFCBadge from '../Badges/MissMFC'
import NewBadge from '../Badges/New'
import PopularBadge from '../Badges/Popular'
import PriorMissMFCBadge from '../Badges/PriorMissMFC'
import StatusBadge from '../Badges/Status'
import TrendingBadge from '../Badges/Trending'
import GroupShowBanner from '../Banners/GroupShow'
import MissMFCBanner from '../Banners/MissMFC'
import NewModelBanner from '../Banners/NewModel'
import PopularBanner from '../Banners/Popular'
import TrendingBanner from '../Banners/Trending'
import BookmarkButton from '../Buttons/BookmarkButton'
import CloseButton from '../Buttons/CloseButton'
import FriendButton from '../Buttons/FriendButton'
import InfoButton from '../Buttons/InfoButton'
import NotesButton from '../Buttons/NotesButton'
import PinButton from '../Buttons/PinButton'
import RoomButton from '../Buttons/RoomButton'
import CellImage from '../CellImage'
import Container from '../Container'
import LargeFloatingPreview from '../LargeFloatingPreview'
import ModelName from '../ModelName'
import { VideoCover } from '../VideoCover'
import {
    GetStylesConfig,
    TIMEOUT_ANIMATION_AVATAR,
    getAnonymousMissMFCRank,
    isAnonymousMissMFC,
    resolveDisplayHighlight,
    resolveModelStatus,
    setLargePreview,
} from '../helpers'

const headerUtilityRootStyles = css({
    justifyContent: 'space-between',
    display: 'flex',
    width: '100%',
})

interface HeaderUtilityProps {
    showLargePreviewButton?: boolean
    onLargeFloatingPinned?: (pinned: boolean) => void
    isLargePreviewPinned?: boolean
    showInfoCloseButton?: boolean
    showBookmarkButton?: boolean
    showFriendButton?: boolean
    showNotesButton?: boolean
    showPinButton?: boolean
    isRoomPinned?: boolean
    onCloseInfo?: (event?: any) => void
    onBookmark?: (event?: any) => void
    onFriend?: (event?: any) => void
    onRoom?: (event?: any) => void
    onPinRoom?: (event?: any) => void
    isBookmark?: boolean
    isFriend?: boolean
    isMember?: boolean
    onOpenNote?: (event?: any) => void
    iconSize?: number
    id: number
}

const HeaderUtility = memo((props: HeaderUtilityProps) => {
    const {
        showLargePreviewButton,
        onLargeFloatingPinned,
        isLargePreviewPinned,
        showInfoCloseButton,
        showBookmarkButton,
        showFriendButton,
        showNotesButton,
        showPinButton,
        isRoomPinned,
        onCloseInfo,
        onBookmark,
        isBookmark,
        onOpenNote,
        onPinRoom,
        onFriend,
        isFriend,
        iconSize,
        onRoom,
        isMember,
        id,
    } = props

    return (
        <div
            css={headerUtilityRootStyles}
            data-testid="Container__HeaderUtility"
        >
            {showLargePreviewButton && (
                <LargeFloatingPreview
                    onPinned={onLargeFloatingPinned}
                    color={palette.backgroundReverse}
                    size={iconSize}
                    filled={isLargePreviewPinned}
                    shadow={false}
                    id={id}
                />
            )}
            {showBookmarkButton && (
                <BookmarkButton
                    color={palette.backgroundReverse}
                    size={iconSize}
                    filled={isBookmark}
                    // style={{
                    //     border: `1px solid ${resolveButtonHighlight({
                    //         isBookmark,
                    //     })}`,
                    // }}
                    title={isBookmark ? 'Unbookmark' : 'Bookmark'}
                    onClick={onBookmark}
                    shadow={false}
                />
            )}
            {showInfoCloseButton && (
                <CloseButton
                    color={palette.backgroundReverse}
                    size={iconSize}
                    onClick={onCloseInfo}
                    shadow={false}
                />
            )}
            {showFriendButton &&
                !showPinButton &&
                !showInfoCloseButton &&
                !showBookmarkButton && (
                    <FriendButton
                        color={palette.backgroundReverse}
                        size={iconSize}
                        filled={isFriend}
                        title={isFriend ? 'Unfriend' : 'Befriend'}
                        onClick={onFriend}
                        shadow={false}
                    />
                )}
            {!isMember && !showNotesButton && (
                <RoomButton
                    color={palette.backgroundReverse}
                    size={iconSize}
                    filled
                    title="Open Room"
                    onClick={onRoom}
                    shadow={false}
                />
            )}
            {showNotesButton && (
                <NotesButton
                    color={palette.backgroundReverse}
                    size={iconSize}
                    filled
                    title="Open Notes"
                    onClick={onOpenNote}
                    shadow={false}
                />
            )}
            {showFriendButton &&
                (showPinButton ||
                    showInfoCloseButton ||
                    showBookmarkButton) && (
                    <FriendButton
                        color={palette.backgroundReverse}
                        size={iconSize}
                        filled={isFriend}
                        title={isFriend ? 'Unfriend' : 'Befriend'}
                        onClick={onFriend}
                        shadow={false}
                    />
                )}
            {showPinButton && (
                <PinButton
                    title={(isRoomPinned ? 'Unpin' : 'Pin') + ' Model'}
                    color={palette.backgroundReverse}
                    size={iconSize}
                    filled={isRoomPinned}
                    shadow={false}
                    onClick={onPinRoom}
                />
            )}
        </div>
    )
})

const resolveNewBadgePosition = (options) => {
    const {
        // canDisplayRecentlyVisitedBadge,
        // canDisplayNotesButton,
        // isLargePreviewPinned,
        // canDisplayInfoButton,
        stylesBadgeStatus,
        // isRoomPinned,
    } = options

    const padding = {
        paddingBottom: stylesBadgeStatus('offsetY') - 1,
        paddingRight: stylesBadgeStatus('offsetX') - 1,
        paddingLeft: stylesBadgeStatus('offsetX') - 1,
        paddingTop: stylesBadgeStatus('offsetY') - 1,
    }

    // // Simply to get around TS issues:
    // // https://github.com/cssinjs/jss/issues/1344
    // const column: React.CSSProperties  = {
    //     flexDirection: 'column',
    // }
    const columnReverse: React.CSSProperties = {
        flexDirection: 'column-reverse',
    }

    // if (
    //     !isRoomPinned &&
    //     !canDisplayNotesButton &&
    //     !canDisplayRecentlyVisitedBadge
    // ) {
    //     return {
    //         ...padding,
    //         ...column,
    //         alignItems: 'flex-end',
    //         right: 0,
    //         top: 0,
    //     }
    // }

    // if (!canDisplayInfoButton && !isLargePreviewPinned) {
    //     return {
    //         ...padding,
    //         ...column,
    //         alignItems: 'flex-start',
    //         left: 0,
    //         top: 0,
    //     }
    // }

    return {
        ...padding,
        ...columnReverse,
        bottom: 0,
        right: 0,
    }
}

const resolveNewBadgeBorderRadius = (options) => {
    const {
        canDisplayRecentlyVisitedBadge,
        canDisplayNotesButton,
        isLargePreviewPinned,
        canDisplayInfoButton,
        isRoomPinned,
    } = options

    if (
        !isRoomPinned &&
        !canDisplayNotesButton &&
        !canDisplayRecentlyVisitedBadge
    ) {
        return {
            borderBottomLeftRadius: 4,
            borderTopRightRadius: 4,
        }
    }

    if (!canDisplayInfoButton && !isLargePreviewPinned) {
        return {
            borderBottomRightRadius: 4,
            borderTopLeftRadius: 4,
        }
    }

    return {
        borderTopLeftRadius: 4,
        borderBottomRightRadius: 4,
    }
}

const rootContainer = css({
    flexDirection: 'column',
    display: 'flex',
})

const avatarContainer = css({
    position: 'relative',
    flexShrink: 0,
})

const headerRootContainerStyles = css({
    alignItems: 'center',
    display: 'flex',
})
const headerContainerStyles = css({
    position: 'absolute',
    alignItems: 'center',
    display: 'flex',
    top: 0,
})
const previewFrameStyles = css({
    transition: 'max-height 0.3s',
    overflow: 'hidden',
    borderRadius: shape.borderRadiusSmallMedium,
    boxShadow: layers.one,
    position: 'relative',
    width: '100%',
})
const newMfcBadgeContainerStyles = css({
    alignItems: 'flex-end',
    position: 'absolute',
    overflow: 'hidden',
    display: 'flex',
    opacity: 0.8,
    zIndex: 1,
})
const statusContainer = css({
    flexDirection: 'column',
    position: 'absolute',
    alignItems: 'center',
    display: 'flex',
    right: 0,
    top: 2,
})
const infoContainer = css({
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'absolute',
    overflow: 'hidden',
    display: 'flex',
    zIndex: 1,
    left: 0,
    top: 0,
})
const badgeRootContainerStyles = css({
    position: 'absolute',
    display: 'flex',
    bottom: 0,
    left: 0,
})
const badgeContainerStyles = css({
    transition: 'margin-bottom 0.3s',
    flexDirection: 'column',
    display: 'flex',
})
const missMfcBadgeStyles = css({
    boxShadow: `#333 0px 1px 2px`,
    borderBottomLeftRadius: 4,
    justifyContent: 'center',
    borderTopRightRadius: 4,
    alignItems: 'center',
    background: '#fff',
    fontWeight: 'bold',
    display: 'flex',
    width: '100%',
    color: '#333',
    opacity: 0.8,
    marginTop: 2,
    padding: '0 2px',
})
const modelMenuContainerStyles = css({
    position: 'absolute',
    bottom: 0,
    right: 0,
})

interface AvatarProps {
    activeRoom?: number
    columnWidth: number
    disableMenuNextPrev?: boolean
    displayBookmarkBadge?: boolean
    displayBookmarkButton?: boolean
    displayBookmarkHighlight?: boolean
    displayFriendBadge?: boolean
    displayFriendButton?: boolean
    displayFriendHighlight?: boolean
    displayGroupShowBanner?: boolean
    displayHeight: number
    displayMissMFCBadge?: boolean
    displayMissMFCBanner?: boolean
    displayNewModelBadge?: boolean
    displayNewModelBanner?: boolean
    displayNoteIconButton?: boolean
    displayPopularBadge?: boolean
    displayPopularBanner?: boolean
    displayPopularHighlight?: boolean
    displayRecentlyVisitedBadge?: boolean
    displayRecentlyVisitedHighlight?: boolean
    displayStatusBadge?: boolean
    displayTrendingBadge?: boolean
    displayTrendingBanner?: boolean
    displayTrendingHighlight?: boolean
    enableLargePreview?: boolean
    enablePin?: boolean
    enablePinToTopSymbols?: boolean
    getStyle: (key: string, config?: GetStylesConfig) => any
    gridPadding: number
    id: number
    loungeSlug?: string
    name: string
    onBookmark?: (id: number, isBookmark: boolean) => boolean
    onFriend?: (id: number, isFriend: boolean) => boolean
    singlePin?: boolean
    pinLimitReached?: boolean
    slug: string
}

const Avatar = memo((props: AvatarProps) => {
    const {
        displayGroupShowBanner,
        displayTrendingBanner,
        displayNewModelBanner,
        displayPopularBanner,
        displayMissMFCBanner,
        displayRecentlyVisitedBadge,
        displayNoteIconButton,
        displayBookmarkBadge,
        displayTrendingBadge,
        displayNewModelBadge,
        displayPopularBadge,
        displayMissMFCBadge,
        displayStatusBadge,
        displayFriendBadge,
        displayRecentlyVisitedHighlight,
        displayTrendingHighlight,
        displayBookmarkHighlight,
        displayPopularHighlight,
        displayFriendHighlight,
        enablePinToTopSymbols,
        onBookmark = () => {},
        onFriend = () => {},
        disableMenuNextPrev,
        enableLargePreview,
        pinLimitReached,
        displayHeight,
        columnWidth,
        gridPadding,
        activeRoom,
        loungeSlug,
        singlePin,
        enablePin,
        getStyle,
        slug,
        name,
        id,
    } = props
    const snapRef = useRef<HTMLImageElement>(undefined)
    const snapSize = useRef<Size & { className: string }>(null)

    const isPinnedToTopRecentlyVisited = useUserSelector(
        id,
        'isPinnedToTopRecentlyVisited',
    )
    const isPinnedToTopTrending = useUserSelector(id, 'isPinnedToTopTrending')
    const isPinnedToTopPopular = useUserSelector(id, 'isPinnedToTopPopular')
    const isPinnedToTopMissMFC = useUserSelector(id, 'isPinnedToTopMissMFC')
    const isBanned = useUserSelector(id, 'banned')
    const isGroupShowPending = useUserSelector(id, 'isGroupShowPending')
    const priorMissMfcRank = useUserSelector(id, 'priorMissMfcRank')
    const groupShowRoomCount = useUserSelector(id, 'groupShowRoomCount')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isTrendingNew = useUserSelector(id, 'isTrendingNew')
    const isTruePrivate = useUserSelector(id, 'isTruePrivate')
    const isPrivateShow = useUserSelector(id, 'isPrivateShow')
    const trendingDiff = useUserSelector(id, 'trendingDiff')
    const missMFCPrize = useUserSelector(id, 'missMFCPrize')
    const isPublicShow = useUserSelector(id, 'isPublicShow')
    const isAnonymous = isAnonymousMissMFC(id)
    const missMFCRankFromSelector = useUserSelector(id, 'missMFCRank')
    const missMFCRank = isAnonymous
        ? getAnonymousMissMFCRank(slug)
        : missMFCRankFromSelector
    const isMissMFCFromSelector = useUserSelector(id, 'isMissMFC')
    const isMissMFC = isAnonymous ? true : isMissMFCFromSelector
    const isGroupShow = useUserSelector(id, 'isGroupShow')
    const isBookmark = useUserSelector(id, 'isBookmark')
    const isNewModel = useUserSelector(id, 'isNewModel')
    const isClubShow = useUserSelector(id, 'isClubShow')
    const isPopular = useUserSelector(id, 'isPopular')
    const isOffline = useUserSelector(id, 'isOffline')
    const isNGVideo = useUserSelector(id, 'isNGVideo')
    const isLounge = useUserSelector(id, 'isLounge')
    const isFriend = useUserSelector(id, 'isFriend')
    const isMember = useUserSelector(id, 'isMember')
    const isKicked = useUserSelector(id, 'isKicked')
    const creation = useUserSelector(id, 'creation')
    const viewers = useUserSelector(id, 'viewers')
    const channel = useUserSelector(id, 'channel')
    const isModel = useUserSelector(id, 'isModel')
    const hasNote = useUserSelector(id, 'hasNote')
    const server = useUserSelector(id, 'server')
    const isAway = useUserSelector(id, 'isAway')
    const phase = useUserSelector(id, 'phase')
    const color = useUserSelector(id, 'color')

    const availableWidth = columnWidth - gridPadding

    const stylesNamePX = getStyle('name', { px: true })
    const stylesBannerPX = getStyle('banner', { px: true })
    const stylesBanner = getStyle('banner')
    const stylesBannerPopular = stylesBanner('popular')
    const stylesBannerPopularViewers = stylesBannerPopular('viewers')
    const stylesBannerTrending = stylesBanner('trending')
    const stylesBannerTrendingDiff = stylesBannerTrending('diff')
    const stylesBannerNew = stylesBanner('new')
    const stylesBannerNewTime = stylesBannerNew('time')
    const stylesBannerMissMFC = stylesBanner('missMFC')
    const stylesBannerMissMFCRank = stylesBannerMissMFC('rank')
    const stylesUtilities = getStyle('utilities')
    const stylesBadge = getStyle('badge')
    const stylesBadgeStatus = stylesBadge('status')
    const stylesBadgeNewMissMFC = stylesBadge('newMissMFC')
    const stylesPriorMissMFCBadge = stylesBadge('priorMissMFC')
    const stylesColumnMaxWidth = getStyle('column')('maxWidth')

    const isInfoOpen = useGridAvatarInfoOpenAppState(id)

    // const [isLargePreviewPinned, setLargePreviewPinned] = useState(false)
    const [canShowRoomPreview, setCanShowRoomPreview] = useState(false)
    const [settingUpRoomPreview, setupRoomPreview] = useState(isInfoOpen)
    const [showRoomPreview, setRoomPreview] = useState(false)
    const hasUserMousedOut = useRef(null)
    const animationRef = useRef(null)
    const animationRecoveryRef = useRef(null)
    const animationDebounceRef = useRef(null)
    const isRoomPinned = useIsRoomPinned(id)

    const inHiddenShow =
        isTruePrivate || isPrivateShow || isGroupShow || isClubShow
    const noShowType = isOffline || inHiddenShow || isAway
    const isViewable = !noShowType && !isBanned && !isKicked
    const showSinglePin = singlePin && isViewable

    const onFriendCallback = useCallback(() => {
        const proceed = onFriend(id, isFriend)

        if (proceed === false) {
            return
        }

        if (isFriend) {
            removeFriend(id)
        } else {
            addFriend(id)
        }
    }, [isFriend, id])

    const onBookmarkCallback = useCallback(() => {
        const proceed = onBookmark(id, isBookmark)

        if (proceed === false) {
            return
        }

        if (isBookmark) {
            removeBookmark(id)
        } else {
            bookmarkModel(id)
        }
    }, [isBookmark, id])

    const onOpenInfoCallback = useCallback(() => {
        setupRoomPreview(true)
        setAvatarInfoOpenID(id)
    }, [id])

    const onCloseInfoCallback = useCallback(() => {
        setCanShowRoomPreview(false)
        setupRoomPreview(false)
        setRoomPreview(false)
        if (isInfoOpen) {
            resetAvatarInfoOpenIDState()
        }
        // setUtilities(false)
    }, [isInfoOpen])

    useEffect(() => {
        clearAvatarInfoOpenTimer(id)

        return () => {
            clearTimeout(animationDebounceRef.current)
            clearTimeout(animationRecoveryRef.current)
            if (isInfoOpen) {
                clearAvatarInfoOpenID(id)
            }
        }
    }, [])

    useEffect(() => {
        if (isInfoOpen) {
            setupRoomPreview(true)
            setCanShowRoomPreview(true)
        } else {
            setCanShowRoomPreview(false)
            setupRoomPreview(false)
            setRoomPreview(false)
        }
    }, [isInfoOpen])

    const onOpenMenuCallback = useCallback(() => {
        onCloseInfoCallback()
        openMenuDialog(slug, {
            hideNextPrev: disableMenuNextPrev,
            transitionSource: TransitionTypes.TILE,
        })
    }, [slug, disableMenuNextPrev])

    const onLargeFloatingPinnedCallback = useCallback(
        (isPinned) => {
            if (isPinned) {
                setLargePreviewPinnedIDState(id)
                return
            }

            setLargePreviewPinnedIDState(null)

            // setLargePreviewPinned(isPinned)
        },
        [id],
    )

    const onGoToRoomCallback = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            if (isAnonymous) return
            onCloseInfoCallback()
            if (loungeSlug) {
                // if we're in the lounge, set the surrogate
                setLoungeSurrogate(id, loungeSlug)
            } else {
                openRoom(slug, {
                    transitionSource: TransitionTypes.TILE,
                })
            }
        },
        [isAnonymous, id, slug, loungeSlug],
    )

    const onPinCallback = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            motionTransition(() => pinRoom(id, { pinned: !isRoomPinned }), {
                id,
                skipTransition: isRoomPinned, // skip if we're unpinning
                transitionSource: TransitionTypes.TILE,
                transitionDestination: TransitionTypes.PINROOM,
            })
            // pinRoom(id, { pinned: !isRoomPinned })
            onCloseInfoCallback()
        },
        [id, isRoomPinned],
    )

    const onOpenNote = useCallback(
        (event) => {
            event.stopPropagation()
            goToNotes(name)
        },
        [id, name],
    )

    // if (!id) {
    //     return <AvatarSkeleton width={width} height={rowHeight} />
    // }

    const modelStatus = resolveModelStatus({
        isTruePrivate,
        isPrivateShow,
        isGroupShow,
        isClubShow,
        isAway,
    })

    //
    // For testing purposes:
    //
    // const candisplayFriendBadge = true
    // const canDisplayBookmarkBadge = true
    // const canDisplayTrendingBadge = true
    // const canDisplayPopularBadge = true
    // const canDisplayNewModelBadge = true
    // const canDisplayMissMFCBadge = true
    // const canDisplayStatusBadge = true
    // const canDisplayPriorMissMFCBadge = true
    // const canDisplayRecentlyVisitedBadge = true
    // const canDisplayNotesButton = true
    // const canEnablePinToTopSymbols = false

    //
    // Animation
    //
    const fadeInPreview =
        settingUpRoomPreview &&
        canShowRoomPreview &&
        (showRoomPreview || !isPublicShow)

    //
    // Symbols
    //
    const canEnablePinToTopSymbols = enablePinToTopSymbols

    //
    // Buttons
    //
    const canDisplayAllHeaderButtons = !isTouchDevice && availableWidth > 177
    const canDisplayInfoButton = isTouchDevice
    const canDisplayInfoCloseButton = canDisplayInfoButton
    const canDisplayLargePreviewButton =
        enableLargePreview && isPublicShow && !canDisplayInfoButton
    const canDisplayBookmarkButton =
        canDisplayAllHeaderButtons ||
        (!canDisplayLargePreviewButton && !canDisplayInfoCloseButton)
    const canDisplayPinButton =
        ((enablePin && !pinLimitReached) || isRoomPinned || showSinglePin) &&
        isPublicShow
    const canDisplayFriendButton =
        canDisplayAllHeaderButtons || !canDisplayPinButton
    const canDisplayNotesButton = hasNote && displayNoteIconButton

    //
    // Banners
    //
    const canDisplayGroupShowBanner =
        displayGroupShowBanner &&
        (isGroupShow || isGroupShowPending || groupShowRoomCount)
    const canDisplayTrendingBanner =
        displayTrendingBanner && isTrendingNew && trendingDiff
    const canDisplayNewModelBanner = displayNewModelBanner && isNewModel
    const canDisplayPopularBanner = displayPopularBanner && isPopular && viewers
    const canDisplayMissMFCBanner =
        displayMissMFCBanner && isMissMFC && missMFCRank

    //
    // Badges
    //
    const candisplayFriendBadge = displayFriendBadge && isFriend
    const canDisplayBookmarkBadge = displayBookmarkBadge && isBookmark
    const canDisplayTrendingBadge =
        displayTrendingBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopTrending) &&
        isTrendingNew &&
        trendingDiff
    const canDisplayPopularBadge =
        displayPopularBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopPopular) &&
        isPopular &&
        viewers
    const canDisplayNewModelBadge = displayNewModelBadge && isNewModel
    const canDisplayMissMFCBadge =
        displayMissMFCBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopMissMFC) &&
        isMissMFC &&
        missMFCRank > 0
    const canDisplayStatusBadge =
        displayStatusBadge && !!modelStatus && !settingUpRoomPreview
    const canDisplayPriorMissMFCBadge = !!priorMissMfcRank
    const canDisplayRecentlyVisitedBadge =
        displayRecentlyVisitedBadge &&
        (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited)

    //
    // Highlights
    //
    const canDisplayActiveRoomHighlight = activeRoom === id
    const canDisplayFriendHighlight = displayFriendHighlight && isFriend
    const canDisplayBookmarkHighlight = displayBookmarkHighlight && isBookmark
    const canDisplayPopularHighlight =
        displayPopularHighlight &&
        isPopular &&
        (!canEnablePinToTopSymbols || isPinnedToTopPopular)
    const canDisplayTrendingHighlight =
        displayTrendingHighlight &&
        isTrendingNew &&
        (!canEnablePinToTopSymbols || isPinnedToTopTrending)
    const canDisplayRecentlyViewedHighlight =
        displayRecentlyVisitedHighlight &&
        (!canEnablePinToTopSymbols || isPinnedToTopRecentlyVisited)

    const highlight = resolveDisplayHighlight({
        displayRecentlyVisitedHighlight: canDisplayRecentlyViewedHighlight,
        displayActiveRoomHighlight: canDisplayActiveRoomHighlight,
        displayTrendingHighlight: canDisplayTrendingHighlight,
        displayBookmarkHighlight: canDisplayBookmarkHighlight,
        displayPopularHighlight: canDisplayPopularHighlight,
        displayFriendHighlight: canDisplayFriendHighlight,
    })

    const canDisplayBanners =
        canDisplayGroupShowBanner ||
        canDisplayTrendingBanner ||
        canDisplayNewModelBanner ||
        canDisplayMissMFCBanner ||
        canDisplayPopularBanner

    const canDisplayBadges =
        canDisplayBookmarkBadge ||
        canDisplayTrendingBadge ||
        canDisplayMissMFCBadge ||
        canDisplayPopularBadge ||
        canDisplayStatusBadge ||
        candisplayFriendBadge

    // const isActiveRoom = getRoomState()[id]?.isSubscribed

    const availableUtilitySpace = (displayHeight - displayHeight * (9 / 16)) / 2

    const badges = useMemo(
        () =>
            [
                Number(canDisplayBookmarkBadge),
                Number(canDisplayTrendingBadge),
                Number(canDisplayPopularBadge),
                Number(candisplayFriendBadge),
            ].reduce((memo, active) => memo + active, 0),
        [
            canDisplayTrendingBadge,
            canDisplayBookmarkBadge,
            canDisplayPopularBadge,
            candisplayFriendBadge,
        ],
    )

    const isLargePreviewPinned =
        useGridLargePreviewPinnedAppState() === id &&
        canDisplayLargePreviewButton

    const dragStart = useCallback(
        (event) => {
            event.dataTransfer.effectAllowed = 'move'
            if (snapRef.current && SNAPVIDEOSTATE[id]?.playing) {
                snapRef.current.width = 128
                snapRef.current.height = 72
                snapRef.current.className = ''
                event.dataTransfer.setDragImage(snapRef.current, 64, 36)
            }
            event.dataTransfer.setData('text/plain', id)
            showDragHelper(id)
        },
        [id],
    )
    const dragMove = useCallback((event) => {
        const { clientX, clientY } = event
        highlightDragHelper(clientX, clientY)
    }, [])
    const dragEnd = useCallback(() => {
        snapRef.current.width = snapSize.current.width
        snapRef.current.height = snapSize.current.height
        snapRef.current.className = snapSize.current.className
        hideDragHelper()
    }, [])
    const onLoad = useCallback((event) => {
        if (snapSize.current) return
        snapRef.current = event.target as HTMLImageElement
        snapSize.current = {
            className: snapRef.current.className,
            width: snapRef.current.width,
            height: snapRef.current.height,
        }
    }, [])

    return (
        <div
            data-testid="Container__ModelDisplay"
            key={id}
            id={`tile-${id}`}
            css={rootContainer}
        >
            <div
                data-testid="Container__Avatar"
                css={avatarContainer}
                style={{
                    height: displayHeight,
                }}
                onMouseOver={() => {
                    if (isTouchDevice) {
                        return
                    }

                    clearTimeout(animationDebounceRef.current)
                    clearTimeout(animationRecoveryRef.current)

                    hasUserMousedOut.current = false

                    if (!animationRef.current) {
                        return
                    }

                    animationDebounceRef.current = setTimeout(() => {
                        setupRoomPreview(true)
                    }, TIMEOUT_ANIMATION_AVATAR)
                }}
                onMouseLeave={() => {
                    if (isTouchDevice) {
                        return
                    }

                    if (!isLargePreviewPinned) {
                        setLargePreview(id, null, false, false)
                    }

                    clearTimeout(animationDebounceRef.current)
                    clearTimeout(animationRecoveryRef.current)

                    hasUserMousedOut.current = true

                    if (!animationRef.current) {
                        return
                    }

                    setCanShowRoomPreview(false)
                    setRoomPreview(false)

                    animationRecoveryRef.current = setTimeout(() => {
                        // sometimes the "onTransitionEnd" event does not fire
                        // after mousing in and out quickly, this will kick in
                        // when that happens
                        setupRoomPreview(false)
                    }, TIMEOUT_ANIMATION_AVATAR * 1.5)
                }}
            >
                {/* <div className="image-gradient-glare" /> */}
                <div
                    css={headerRootContainerStyles}
                    style={{
                        height: displayHeight,
                    }}
                >
                    {!isAnonymous && (
                        <div
                            css={headerContainerStyles}
                            style={{
                                visibility: settingUpRoomPreview
                                    ? null
                                    : 'hidden',
                                height: availableUtilitySpace,
                                width: availableWidth,
                            }}
                            data-testid="Container__AvatarRoomPreview"
                        >
                            <HeaderUtility
                                showLargePreviewButton={
                                    canDisplayLargePreviewButton
                                }
                                onLargeFloatingPinned={
                                    onLargeFloatingPinnedCallback
                                }
                                showInfoCloseButton={canDisplayInfoCloseButton}
                                showBookmarkButton={canDisplayBookmarkButton}
                                isLargePreviewPinned={isLargePreviewPinned}
                                showFriendButton={canDisplayFriendButton}
                                showNotesButton={canDisplayNotesButton}
                                iconSize={stylesUtilities('iconSize')}
                                showPinButton={canDisplayPinButton}
                                onCloseInfo={onCloseInfoCallback}
                                onBookmark={onBookmarkCallback}
                                onFriend={onFriendCallback}
                                onRoom={onGoToRoomCallback}
                                isRoomPinned={isRoomPinned}
                                onPinRoom={onPinCallback}
                                onOpenNote={onOpenNote}
                                isBookmark={isBookmark}
                                isFriend={isFriend}
                                isMember={isMember}
                                id={id}
                            />
                        </div>
                    )}
                    <div
                        css={previewFrameStyles}
                        style={{
                            border: highlight ? `1px solid ${highlight}` : null,
                            maxHeight: settingUpRoomPreview
                                ? displayHeight * (9 / 16)
                                : displayHeight,
                            height: displayHeight,
                        }}
                        onTransitionEnd={() => {
                            if (hasUserMousedOut.current) {
                                setupRoomPreview(false)
                                return
                            }

                            setCanShowRoomPreview(true)
                        }}
                        ref={animationRef}
                    >
                        {(settingUpRoomPreview || isAnonymous) && (
                            <Fade
                                timeout={TIMEOUT_ANIMATION_AVATAR}
                                // onEntered={() => {
                                //     setUtilities(true)
                                // }}
                                // onExit={() => {
                                //     setUtilities(false)
                                // }}
                                in={fadeInPreview}
                                appear={false}
                            >
                                <div
                                    onClick={onGoToRoomCallback}
                                    draggable
                                    onDragStart={dragStart}
                                    onDrag={dragMove}
                                    onDragEnd={dragEnd}
                                >
                                    <VideoCover />
                                    <LiveSnapStream
                                        styleOverrides={{
                                            // border: fadeInPreview && highlight ? `1px solid #fff` : null,
                                            borderRadius:
                                                fadeInPreview && highlight
                                                    ? shape.borderRadiusSmallMedium
                                                    : 0,
                                            position: 'absolute',
                                            overflow: 'hidden',
                                            fontSize: 10,
                                            top: 0,
                                        }}
                                        onClick={onGoToRoomCallback}
                                        banned={isBanned}
                                        channel={channel}
                                        height={displayHeight * (9 / 16)}
                                        hideControls
                                        isAway={isAway}
                                        isBroadcasting={isBroadcasting}
                                        isClubShow={isClubShow}
                                        isGroupShow={isGroupShow}
                                        isLounge={isLounge}
                                        isModel={isModel}
                                        isNGVideo={isNGVideo}
                                        isOffline={isOffline}
                                        isPrivateShow={isPrivateShow}
                                        isTruePrivate={isTruePrivate}
                                        // force re-creation if slug changes
                                        key={`model-tile-snap-stream-${slug}`}
                                        id={id}
                                        phase={phase}
                                        server={server}
                                        videoID={VideoType.ModelTile}
                                        width={columnWidth}
                                        isAnonymous={isAnonymous}
                                        onPreviewLoaded={(event) => {
                                            if (hasUserMousedOut.current) {
                                                return
                                            }
                                            onLoad(event)
                                            setRoomPreview(true)
                                        }}
                                        onVideoPlaying={() => {
                                            if (hasUserMousedOut.current) {
                                                return
                                            }
                                            setRoomPreview(true)
                                        }}
                                        compact={stylesColumnMaxWidth < 150}
                                        noBackground
                                        videoMuted
                                    >
                                        {null}
                                    </LiveSnapStream>
                                </div>
                            </Fade>
                        )}
                        <Fade
                            timeout={TIMEOUT_ANIMATION_AVATAR}
                            in={!fadeInPreview}
                            appear={false}
                        >
                            <div
                                onClick={onGoToRoomCallback}
                                draggable
                                onDragStart={dragStart}
                                onDragEnd={dragEnd}
                            >
                                <CellImage
                                    styleOverrides={{
                                        position: 'relative',
                                    }}
                                    size={
                                        stylesColumnMaxWidth <= 150 ? 150 : 300
                                    }
                                    slug={slug}
                                    id={id}
                                />
                            </div>
                        </Fade>
                        {(canDisplayNewModelBadge ||
                            (canDisplayPriorMissMFCBadge &&
                                !canDisplayMissMFCBadge)) && (
                            <div
                                css={newMfcBadgeContainerStyles}
                                style={{
                                    fontSize: stylesBadgeStatus('fontSize'),
                                    ...resolveNewBadgePosition({
                                        canDisplayRecentlyVisitedBadge,
                                        canDisplayNotesButton,
                                        isLargePreviewPinned,
                                        canDisplayInfoButton,
                                        stylesBadgeStatus,
                                        isRoomPinned,
                                    }),
                                }}
                            >
                                {canDisplayNewModelBadge && (
                                    <NewBadge
                                        styleOverrides={{
                                            boxShadow: canDisplayNewModelBadge
                                                ? `#333 0px 1px 2px`
                                                : null,
                                            background: canDisplayNewModelBadge
                                                ? '#fff'
                                                : null,
                                            ...resolveNewBadgeBorderRadius({
                                                canDisplayRecentlyVisitedBadge,
                                                canDisplayNotesButton,
                                                isLargePreviewPinned,
                                                canDisplayInfoButton,
                                                isRoomPinned,
                                            }),
                                        }}
                                    />
                                )}
                                {canDisplayPriorMissMFCBadge && (
                                    <PriorMissMFCBadge
                                        styleOverrides={{
                                            height: stylesPriorMissMFCBadge(
                                                'altHeight',
                                            ),
                                            width: stylesPriorMissMFCBadge(
                                                'altWidth',
                                            ),
                                            margin: canDisplayNewModelBadge
                                                ? '0 2px 4px 0'
                                                : null,

                                            // stylesPriorMissMFCBadge(
                                            //     canDisplayNewModelBadge ?
                                            //     'margin' :
                                            //     'altMargin',
                                            // ),
                                        }}
                                        priorMissMfcRank={priorMissMfcRank}
                                    />
                                )}
                            </div>
                        )}
                        {(isRoomPinned ||
                            canDisplayNotesButton ||
                            canDisplayRecentlyVisitedBadge) &&
                            !settingUpRoomPreview && (
                                <div css={statusContainer}>
                                    {isRoomPinned && !isTouchDevice && (
                                        <Container
                                            styleOverrides={{
                                                opacity: 0.8,
                                                padding: 4,
                                            }}
                                            shadow
                                        >
                                            <PinIcon
                                                size={stylesUtilities(
                                                    'avatarIconSize',
                                                )}
                                                color="#fff"
                                                filled
                                            />
                                        </Container>
                                    )}
                                    {isRoomPinned && isTouchDevice && (
                                        <PinButton
                                            title={
                                                (isRoomPinned
                                                    ? 'Unpin'
                                                    : 'Pin') + ' Model'
                                            }
                                            size={stylesUtilities('iconSize')}
                                            filled={isRoomPinned}
                                            shadow
                                            onClick={onPinCallback}
                                        />
                                    )}
                                    {canDisplayNotesButton &&
                                        !isTouchDevice && (
                                            <Container
                                                styleOverrides={{
                                                    opacity: 0.8,
                                                    padding: 4,
                                                }}
                                                shadow
                                            >
                                                <NotesIcon
                                                    size={stylesUtilities(
                                                        'avatarIconSize',
                                                    )}
                                                    color="#fff"
                                                    filled
                                                />
                                            </Container>
                                        )}
                                    {canDisplayNotesButton && isTouchDevice && (
                                        <NotesButton
                                            styleOverrides={{
                                                marginLeft: -3,
                                                marginTop: -3,
                                            }}
                                            size={stylesUtilities(
                                                'avatarIconSize',
                                            )}
                                            filled
                                            title="Open Notes"
                                            onClick={onOpenNote}
                                        />
                                    )}
                                    {canDisplayRecentlyVisitedBadge && (
                                        <Container
                                            title="Recently Visited"
                                            styleOverrides={{
                                                opacity: 0.8,
                                                padding: 4,
                                            }}
                                            tooltipPlacement="bottom"
                                            shadow
                                        >
                                            <RoomIcon
                                                size={stylesUtilities(
                                                    'avatarIconSize',
                                                )}
                                                color="#fff"
                                                filled
                                            />
                                        </Container>
                                    )}
                                </div>
                            )}
                        {(canDisplayInfoButton || isLargePreviewPinned) &&
                            !settingUpRoomPreview && (
                                <div css={infoContainer}>
                                    {canDisplayInfoButton && (
                                        <InfoButton
                                            styleOverrides={{
                                                visibility: settingUpRoomPreview
                                                    ? 'hidden'
                                                    : null,
                                                marginLeft: -3,
                                                marginTop: -3,
                                                opacity: 0.75,
                                            }}
                                            size={stylesUtilities(
                                                'avatarIconSize',
                                            )}
                                            onClick={onOpenInfoCallback}
                                        />
                                    )}
                                    {isLargePreviewPinned &&
                                        !settingUpRoomPreview && (
                                            <Container
                                                styleOverrides={{
                                                    padding: 4,
                                                }}
                                            >
                                                <CameraIcon
                                                    size={stylesUtilities(
                                                        'avatarIconSize',
                                                    )}
                                                    color="#fff"
                                                    filled
                                                />
                                            </Container>
                                        )}
                                </div>
                            )}
                    </div>
                </div>
                {canDisplayBadges && (
                    <div css={badgeRootContainerStyles}>
                        <div
                            css={badgeContainerStyles}
                            style={{
                                marginBottom:
                                    settingUpRoomPreview &&
                                    (canDisplayStatusBadge ||
                                        canDisplayMissMFCBadge)
                                        ? 0
                                        : stylesBadgeStatus('offsetY'),
                                marginLeft: stylesBadgeStatus('offsetX'),
                            }}
                        >
                            {(candisplayFriendBadge ||
                                canDisplayBookmarkBadge ||
                                canDisplayTrendingBadge ||
                                canDisplayPopularBadge) && (
                                <div
                                    style={{
                                        justifyContent:
                                            badges === 1
                                                ? 'flex-start'
                                                : 'center',
                                        display: 'flex',
                                    }}
                                >
                                    {candisplayFriendBadge && (
                                        <FriendBadge
                                            size={stylesBadgeStatus('iconSize')}
                                        />
                                    )}
                                    {canDisplayBookmarkBadge && (
                                        <BookmarkBadge
                                            size={stylesBadgeStatus('iconSize')}
                                        />
                                    )}
                                    {canDisplayPopularBadge && (
                                        <PopularBadge
                                            size={stylesBadgeStatus('iconSize')}
                                        />
                                    )}
                                    {canDisplayTrendingBadge && (
                                        <TrendingBadge
                                            size={stylesBadgeStatus('iconSize')}
                                        />
                                    )}
                                </div>
                            )}
                            {canDisplayMissMFCBadge && (
                                <div style={{ display: 'flex' }}>
                                    {canDisplayMissMFCBadge && (
                                        <div
                                            css={missMfcBadgeStyles}
                                            style={{
                                                fontSize:
                                                    stylesBadgeStatus(
                                                        'fontSize',
                                                    ),
                                            }}
                                        >
                                            <MissMFCBadge
                                                compact={stylesBadgeNewMissMFC(
                                                    'compact',
                                                )}
                                                missMFCRank={missMFCRank}
                                            />
                                        </div>
                                    )}
                                    {canDisplayPriorMissMFCBadge && (
                                        <PriorMissMFCBadge
                                            styleOverrides={{
                                                height: stylesPriorMissMFCBadge(
                                                    'height',
                                                ),
                                                width: stylesPriorMissMFCBadge(
                                                    'width',
                                                ),
                                                marginLeft: 2,
                                                marginTop: 2,
                                            }}
                                            priorMissMfcRank={priorMissMfcRank}
                                        />
                                    )}
                                </div>
                            )}
                            {!!canDisplayStatusBadge && (
                                <StatusBadge
                                    styleOverrides={{
                                        transition:
                                            'max-height 0.2s, max-width 0.2s',
                                        maxWidth: settingUpRoomPreview
                                            ? 36
                                            : 100,
                                        maxHeight: settingUpRoomPreview
                                            ? 0
                                            : 100,
                                        fontSize: stylesBadgeStatus('fontSize'),
                                        overflow: 'hidden',
                                        padding: '0 2px',
                                        marginTop: 2,
                                    }}
                                    status={modelStatus}
                                    shadow
                                />
                            )}
                        </div>
                    </div>
                )}
                <div css={modelMenuContainerStyles}>
                    <Grow
                        in={
                            fadeInPreview &&
                            (!hasUserMousedOut.current || isTouchDevice) &&
                            !isAnonymous
                        }
                    >
                        <div
                            style={{
                                // visibility:
                                //     fadeInPreview &&
                                //     (!hasUserMousedOut.current || isTouchDevice) &&
                                //     !isAnonymous
                                //         ? null
                                //         : 'hidden',
                                marginLeft: 'auto',
                                marginBottom: getStyle('menu')('offsetY'),
                            }}
                        >
                            <ButtonModelMenu onClick={onOpenMenuCallback} />
                        </div>
                    </Grow>
                </div>
            </div>
            {canDisplayBanners && (
                <>
                    {canDisplayMissMFCBanner && (
                        <MissMFCBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                            }}
                            rankStyleOverrides={{
                                margin: stylesBannerMissMFCRank('margin'),
                            }}
                            text={stylesBannerMissMFC('text')}
                            missMFCPrize={missMFCPrize}
                            missMFCRank={missMFCRank}
                        />
                    )}
                    {canDisplayTrendingBanner && (
                        <TrendingBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                            }}
                            diffStyleOverrides={{
                                margin: stylesBannerTrendingDiff('margin'),
                            }}
                            hideIcon={
                                availableWidth < stylesBanner('minimumWidth')
                            }
                            text={stylesBannerTrending('text')}
                            diff={trendingDiff}
                        />
                    )}
                    {canDisplayPopularBanner && (
                        <PopularBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                            }}
                            viewersStyleOverrides={{
                                margin: stylesBannerPopularViewers('margin'),
                            }}
                            hideIcon={
                                availableWidth < stylesBanner('minimumWidth')
                            }
                            iconSize={stylesBanner('iconSize')}
                            text={stylesBannerPopular('text')}
                            viewers={viewers}
                        />
                    )}
                    {canDisplayGroupShowBanner &&
                        (isGroupShow ||
                            isGroupShowPending ||
                            groupShowRoomCount) && (
                            <GroupShowBanner
                                styleOverrides={{
                                    fontSize: stylesBannerPX('fontSize'),
                                    height: stylesBannerPX('height'),
                                }}
                                iconSize={stylesBanner('iconSize')}
                                roomCount={groupShowRoomCount}
                                isLive={isGroupShow}
                            />
                        )}
                    {canDisplayNewModelBanner && (
                        <NewModelBanner
                            styleOverrides={{
                                fontSize: stylesBannerPX('fontSize'),
                                height: stylesBannerPX('height'),
                            }}
                            timeStyleOverrides={{
                                margin: stylesBannerNewTime('margin'),
                            }}
                            compact={columnWidth < 135}
                            creation={creation}
                        />
                    )}
                </>
            )}
            <ModelName
                onClick={onGoToRoomCallback}
                modelId={id}
                styleOverrides={{
                    ...(canDisplayBanners
                        ? { borderTop: `1px solid ${palette.drawerDivider}` }
                        : {}),
                    //`1px dashed ${hexRGBA(color, 0.05)}`,
                    justifyContent: canDisplayBanners ? 'center' : 'flex-start',
                    // marginLeft: 8,
                    fontSize: stylesNamePX('fontSize')(),
                    height: stylesNamePX('height'),
                    borderBottomRightRadius: 6,
                    borderBottomLeftRadius: 0,
                    alignItems: 'center',
                }}
                isAnonymous={isAnonymous}
                color={color}
                name={name}
            />
        </div>
    )
})

export default Avatar
